<template>
  <div v-if="loaded">
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push(`/${module}`)"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn
          style="float: right"
          class="capitalize ctrl-button right q-ml-md"
          :color="readOnly ? 'grey-5' : 'ctrl-turquoise'"
          :label="
            readOnly
              ? $store.state.stringsStore.strings.action.edit
              : $store.state.stringsStore.strings.action.save
          "
          @click="save()"
          :icon-right="readOnly ? 'edit' : 'save'"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">
      <!-- Left -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ getString(`module.name.emails`) }}:
              {{ getString(`module.emails.code.${data.code}`) }}
            </p>

            <div class="card-block">
              <div class="row">
                <div class="q-if-label q-if-label-above">
                  <div class="q-if-label-inner ellipsis">
                    {{
                      $store.state.stringsStore.language === 'en'
                        ? 'Subject'
                        : 'Onderwerp'
                    }}
                  </div>
                </div>
                <br />
                <q-editor
                  :disable="readOnly"
                  ref="subject"
                  class="full-width q-mb-md"
                  v-model="subject"
                  min-height="4rem"
                  max-height="4rem"
                  @keydown.native.enter.prevent
                  @keyup.native="saveRange('subject')"
                  @click.native="saveRange('subject')"
                  @input="editorUpdate('subject')"
                  :toolbar="[]"
                />
              </div>
              <div class="row">

              </div>
              <div class="row">
                <div class="q-if-label q-if-label-above">
                  <div class="q-if-label-inner ellipsis">Content</div>
                </div>
                <br />
                <q-editor
                  class="full-width q-mb-md"
                  :disable="readOnly"
                  ref="content"
                  v-model="content"
                  @keyup.native="saveRange('content')"
                  @click.native="saveRange('content')"
                  @fullscreen="addVariablesOnToolbar"
                  @keydown.native.enter.prevent="addLine"
                  @input="editorUpdate('content')"
                  :toolbar="toolbar"
                >
                  <template v-slot:variables>
                    <q-btn-dropdown
                      dense
                      no-caps
                      ref="variables"
                      no-wrap
                      unelevated
                      label="Variables"
                      size="m"
                    >
                      <q-list link>
                        <q-item
                          tag="label"
                          v-for="variable in variables"
                          :key="variable"
                          clickable
                          @click.native="addVariable(variable)"
                        >
                          <q-item-main>{{ variable }}</q-item-main>
                        </q-item>
                      </q-list>
                    </q-btn-dropdown>
                  </template>
                </q-editor>
              </div>
            </div>
          </div>
        </div>
      </div>


      <!-- Right -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{
                $store.state.stringsStore.language === 'en'
                  ? 'Variables'
                  : 'Variabelen'
              }}</p>
              <div class="card-block">
                <q-list link>
                  <div v-if="data.variables">
                    <variable-tag
                      v-for="variable in variables"
                      :disable="readOnly"
                      :key="variable"
                      :name="variable"
                      :onlyReadable="true"
                      :customText="customText(variable)"
                      @click.native="addVariable(variable)"
                      @onEditText="editCustomVariable"
                    >
                    </variable-tag>
                  </div>
                </q-list>
              </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

// Mixins
import getString from '@/helpers/GetStrings';
import suggestUrl from '@/mixins/suggestUrl';
import saveChanges from '@/mixins/detailViews/saveChanges';
import { required, email } from 'vuelidate/lib/validators';
import common from '@/mixins/library/common';
import VariableTag from '@/components/VariableTag.vue';

export default {
  mixins: [suggestUrl, saveChanges, common],
  components: {
    VariableTag,
  },
  data() {
    return {
      successMessage: this.getString(null, 'Successfully saved the email template', 'De e-mail is succesvol opgeslagen'),
      loaded: false,
      readOnly: true,
      containsErrors: true,
      module: 'emails',
      endpoint: 'emails',
      inputSelected: '',
      mailSent: true,
      isEmailValid: true,
      currentRange: {},
      toolbar: [
        [
          'bold',
          'italic',
          'strike',
          'underline',
          'removeFormat',
          'unordered',
          'ordered',
        ],
        ['link'],
        ['undo', 'redo'],
        [
          {
            label: this.$q.i18n.editor.formatting,
            icon: this.$q.icon.editor.formatting,
            list: 'no-icons',
            options: ['p', 'h6', 'h5', 'h4', 'h3', 'h2'],
          },
        ],
        ['fullscreen'],
      ],
      data: {
        id: null,
        subject: '',
        content: '',
        variables: null,
        custom_variables: null,
        code: '',
      },
      content: '',
      subject: '',
      labelSet: false,
    };
  },
  validations: {
    email: { required, email },
  },
  methods: {
    addLine() {
      const edit = this.$refs[this.inputSelected];
      edit.runCmd('insertParagraph');
    },
    addVariablesOnToolbar(state) {
      state ? this.toolbar.push(['variables']) : this.toolbar.pop();
    },
    editorUpdate(field) {
      const toClear = field === 'subject' ? 'br,span' : 'span';
      this.$refs[field]
        .getContentEl()
        .querySelectorAll(toClear)
        .forEach((el) => {
          if (!el.textContent.trim()) {
            el.parentNode.removeChild(el);
          }
        });
      let text = this.$refs[field].getContentEl().innerHTML;
      const htmlEl = document.createElement('div');
      htmlEl.innerHTML = text.trim();
      this.variables.forEach((name) => {
        const varComponent = htmlEl.getElementsByClassName(
          `variables__${name.toLowerCase().split(' ').join('-')}`,
        );
        if (varComponent.length) {
          text = text.replaceAll(varComponent[0].outerHTML, `%${name}%`);
        }
      });
      this.data[field] = text.trim().replace(/&nbsp;/g, ' ');
    },
    getString,
    addVariable(name) {
      if (!this.inputSelected) return;
      const edit = this.$refs[this.inputSelected];
      edit.caret.restore(this.currentRange[this.inputSelected]);
      edit.runCmd('insertText', '%to__replace%');
      const data = edit
        .getContentEl()
        .innerHTML.replaceAll('%to__replace%', this.createVariableTag(name));
      const scrollTop = document.getElementsByTagName('main')[0].scrollTop;
      edit.runCmd('selectAll');
      edit.runCmd('delete');
      edit.runCmd('insertHTML', data);
      document.getElementsByTagName('main')[0].scrollTop = scrollTop;
      edit.focus();
    },
    saveRange(inputSelected) {
      this.inputSelected = inputSelected;
      const edit = this.$refs[this.inputSelected];
      this.currentRange[this.inputSelected] = edit.caret.range;
    },
    createVariableTag(name) {
      const VariableTagComponent = Vue.extend(VariableTag);
      const tag = new VariableTagComponent({ propsData: { name } });
      tag.$mount();
      return tag.$el.outerHTML;
    },
    requestParams() {
      const editors = ['content', 'subject'];
      editors.forEach(n => this.editorUpdate(n));
      return this.data;
    },
    save() {
      if (this.readOnly) {
        this.readOnly = false;
        return;
      }
      this.saveChanges();
      this.readOnly = !this.readOnly;
    },
    customText(name) {
      const variables = this.customVariables;
      const key = Object.keys(variables).find(k => k.includes(name));
      const toReturn = {};
      toReturn[key] = variables[key];
      return key ? toReturn : undefined;
    },
    editCustomVariable(key, value) {
      const variables = this.customVariables;
      variables[key] = value;
      this.data.custom_variables = JSON.stringify(variables);
    },
  },
  computed: {
    variables() {
      return this.data.variables ? JSON.parse(this.data.variables) : [];
    },
    customVariables() {
      return this.data.custom_variables
        ? JSON.parse(this.data.custom_variables)
        : [];
    },
  },
  destroyed() {
    this.$store.commit('setReloadEmail', true);
  },
  async mounted() {
    if (this.$store.getters.reloadEmail) {
      this.$router.go();
    }
    this.$store.commit('setReloadEmail', false);

    if (this.$route.params.id) {
      this.axios
        .get(`${this.endpoint}/${this.$route.params.id}`)
        .then((res) => {
          this.data = res.data[this.endpoint];
          let content = this.data.content;
          let subject = this.data.subject;
          Array.from(this.variables).forEach((v) => {
            content = content.replaceAll(`%${v}%`, this.createVariableTag(v));
            subject = subject.replaceAll(`%${v}%`, this.createVariableTag(v));
          });
          this.content = content;
          this.subject = subject;
          this.loaded = true;
        });
    } else {
      this.readOnly = false;
    }
  },
};
</script>

<style lang="stylus" scoped>
.sticky {
  position: sticky;
  top: 70px;
}

td, th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
}

</style>
